import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { TUTORIAL_EVENT, TUTORIAL_RESTART, type TutorialTask } from './events'
import './tutorial.css'

type Progress = {
  status: 'offered' | 'active' | 'skipped' | 'completed'
  index: number
  done: Partial<Record<TutorialTask, boolean>>
  groupId?: string
}
export type TutorialStep = { task: TutorialTask; title: string; text: string; path?: string; target: string }
const fresh = (): Progress => ({ status: 'offered', index: 0, done: {} })
function read(key: string): Progress {
  try {
    const p = JSON.parse(localStorage.getItem(key) || 'null')
    if (p && ['offered', 'active', 'skipped', 'completed'].includes(p.status) && Number.isInteger(p.index) && p.index >= 0 && p.index < 5 && p.done && typeof p.done === 'object') return p
  } catch { /* Хранилище недоступно: обучение работает до закрытия страницы. */ }
  return fresh()
}

/** Подсказки не перехватывают клики: пользователь работает с настоящими формами. */
export function GuidedTutorial({ owner, offer, steps, knownDone = [], setup = false, bot = false, openBot }: {
  owner: string
  offer: boolean
  steps: TutorialStep[]
  knownDone?: TutorialTask[]
  setup?: boolean
  bot?: boolean
  openBot?: () => void
}) {
  const key = `procoach:tutorial:v1:${owner}`
  const [exitConfirmation, setExitConfirmation] = useState(false)
  const exitDialog = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState<Progress>(() => read(key))
  const progressRef = useRef(progress)
  progressRef.current = progress
  const [visible, setVisible] = useState(offer || read(key).status === 'active')
  const navigate = useNavigate()
  const step = steps[progress.index]
  const active = visible && progress.status === 'active'
  const done = !!step && (!!progress.done[step.task] || knownDone.includes(step.task))

  useEffect(() => { if (offer) setVisible(true) }, [offer])
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(progress)) } catch { /* См. read. */ }
  }, [key, progress])
  useEffect(() => {
    const complete = (event: Event) => {
      const { task, resourceId } = (event as CustomEvent<{ task: TutorialTask; resourceId?: string }>).detail
      if (!['direction', 'group', 'schedule', 'athlete', 'hub'].includes(task)) return
      const p = progressRef.current
      const updated = { ...p, done: { ...p.done, [task]: true }, ...(task === 'group' && resourceId ? { groupId: resourceId } : {}) }
      progressRef.current = updated
      // Настройка может сразу размонтировать обучение: сохраняем до смены экрана.
      try { localStorage.setItem(key, JSON.stringify(updated)) } catch { /* См. read. */ }
      setProgress(updated)
    }
    const restart = () => { setProgress({ ...fresh(), status: 'active' }); setVisible(true) }
    window.addEventListener(TUTORIAL_EVENT, complete)
    window.addEventListener(TUTORIAL_RESTART, restart)
    return () => { window.removeEventListener(TUTORIAL_EVENT, complete); window.removeEventListener(TUTORIAL_RESTART, restart) }
  }, [key])

  useEffect(() => {
    if (!active || !step || done) return
    const highlighted = new Set<Element>()
    const highlight = () => {
      document.querySelectorAll(step.target).forEach(el => {
        if (!el.closest('.pc-tutorial')) { el.classList.add('pc-tutorial-target'); highlighted.add(el) }
      })
    }
    highlight()
    const observer = new MutationObserver(highlight)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => { observer.disconnect(); highlighted.forEach(el => el.classList.remove('pc-tutorial-target')) }
  }, [active, step?.target, done])

  useEffect(() => {
    if (!exitConfirmation) return
    const previousFocus = document.activeElement as HTMLElement | null
    const dialog = exitDialog.current
    const buttons = Array.from(dialog?.querySelectorAll<HTMLButtonElement>('button') ?? [])
    buttons[0]?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setExitConfirmation(false) }
      if (e.key === 'Tab') {
        e.preventDefault()
        e.stopPropagation()
        const current = buttons.indexOf(document.activeElement as HTMLButtonElement)
        buttons[(current + (e.shiftKey ? -1 : 1) + buttons.length) % buttons.length]?.focus()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => { document.removeEventListener('keydown', onKey, true); previousFocus?.isConnected && previousFocus.focus() }
  }, [exitConfirmation])

  const open = () => {
    if (setup) return
    if (step.task === 'hub' && bot) { openBot?.(); return }
    const path = step.task === 'schedule' && progress.groupId && step.path?.includes(':groupId')
      ? step.path.replace(':groupId', progress.groupId) : step.path
    if (path && !path.includes(':groupId')) navigate(path)
    else if (step.task === 'schedule') navigate('/groups')
  }
  const next = () => setProgress(p => p.index >= steps.length - 1 ? { ...p, status: 'completed' } : { ...p, index: p.index + 1 })

  if (!visible || progress.status === 'skipped') return null
  if (progress.status === 'completed') return <section className="pc-tutorial" aria-label="Обучение завершено">
    <strong>Вы освоили основные шаги!</strong><p>Обучение можно повторить в разделе «Клуб».</p>
    <button onClick={() => setVisible(false)}>К работе</button>
  </section>
  if (progress.status === 'offered') return <section className="pc-tutorial" aria-label="Знакомство с ProCoach">
    <strong>Освоим ProCoach вместе?</strong>
    <p>Создадим направление, группу, расписание и спортсмена, затем спросим Штаб голосом или текстом. Вы работаете со своими данными - всё сохранится в клубе.</p>
    <div className="pc-tutorial-actions"><button onClick={() => setProgress(p => ({ ...p, status: 'active' }))}>Пройти обучение</button>
      <button onClick={() => setProgress(p => ({ ...p, status: 'skipped' }))}>Пропустить обучение</button></div>
  </section>
  if (!step) return null
  return <section className="pc-tutorial" aria-label="Обучение ProCoach">
    {exitConfirmation && <div className="pc-tutorial-confirm-backdrop">
      <div ref={exitDialog} className="pc-tutorial-confirm" role="alertdialog" aria-modal="true" aria-labelledby="tutorial-exit-title" aria-describedby="tutorial-exit-text">
        <strong id="tutorial-exit-title">Точно завершить обучение?</strong>
        <p id="tutorial-exit-text">Введённые данные сохранятся. Обучение можно запустить заново в разделе «Клуб».</p>
        <div className="pc-tutorial-actions"><button onClick={() => setExitConfirmation(false)}>Продолжить обучение</button>
          <button onClick={() => { setExitConfirmation(false); setProgress(p => ({ ...p, status: 'skipped' })) }}>Завершить и выйти</button></div>
      </div>
    </div>}
    <div className="pc-tutorial-heading"><strong>Шаг {progress.index + 1} из {steps.length}: {step.title}</strong>
      <button className="pc-tutorial-exit" aria-label="Закрыть обучение" onClick={() => setExitConfirmation(true)}>×</button></div>
    <p>{step.text}</p>
    {setup && progress.index > 1 && <p>Сначала завершите начальную настройку или нажмите «Отложить» под формой группы.</p>}
    <p role="status">{done ? 'Готово! Можно продолжать.' : 'Выполните действие в подсвеченной форме.'}</p>
    <div className="pc-tutorial-actions">
      {!done && !setup && <button onClick={open}>{step.task === 'hub' && bot ? 'Открыть бота' : 'Открыть шаг'}</button>}
      <button disabled={!done && !(bot && step.task === 'hub')} onClick={next}>{bot && step.task === 'hub' && !done ? 'Ответ получен в боте' : progress.index === steps.length - 1 ? 'Завершить обучение' : 'Дальше'}</button>
      {progress.index > 0 && <button onClick={() => setProgress(p => ({ ...p, index: p.index - 1 }))}>Назад</button>}
      <button onClick={next}>Пропустить шаг</button>
    </div>
  </section>
}
